import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Country, State, City } from 'country-state-city';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Loader from './Loader';
import PhoneInput from 'react-phone-number-input';
import CustomPhoneInput from './CustomPhoneInput';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Modal from 'react-bootstrap/Modal';
import VirtualizedAutocomplete from './Autocomplete';
import Backdrop from '@mui/material/Backdrop';
import dayjs from 'dayjs';
import 'react-phone-number-input/style.css';

const Form = ({ show = false, title = "", isLoading = false, columns = [], selectedRow = {}, isWritable = false, dropdowns = {}, rowType = null, handleSubmitEditedRow = () => { }, handleSubmitAddedRow = () => { }, handleClose = () => { } }) => {
    const [countryIso, setCountryIso] = useState("");
    const [stateIso, setStateIso] = useState("");
    const [formData, setFormData] = useState({});
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const phoneInputRef = useRef(null);
    const submitButtonRef = useRef(null);

    useEffect(() => {
        if (Object.keys(selectedRow).length > 0) {
            setFormData(selectedRow);

            if (selectedRow.Country) {
                const foundCountry = Country.getAllCountries().find((c) => c.name === selectedRow.Country);

                if (foundCountry) {
                    setCountryIso(foundCountry.isoCode);

                    if (selectedRow.State) {
                        const foundState = State.getStatesOfCountry(foundCountry.isoCode).find((s) => s.name === selectedRow.State);

                        if (foundState) {
                            setStateIso(foundState.isoCode);
                        }
                    }
                }
            }
        }
    }, [selectedRow]);

    const convertToDayJsObject = (date) => {
        return date ? dayjs(date, "YYYY-MM-DD HH:mm:ss") : null;
    }

    const handleValidation = useCallback((field, value) => {
        if (["Email_Address", "Email"].includes(field)) {
            const emailPattern = new RegExp(/^(('[\w\s-]+')|([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);

            if (value && !emailPattern.test(value)) {
                setValidationErrors((prev) => {
                    if (prev[field] === "Please enter a valid email.") return prev;
                    return { ...prev, [field]: "Please enter a valid email." };
                });
            }
            else {
                setValidationErrors((prev) => {
                    if (!(field in prev)) return prev;
                    const updated = { ...prev };
                    delete updated[field];
                    return updated;
                });
            }
        }
        else if (["Mobile_Number", "Contact_Number"].includes(field)) {
            if (value) {
                const mobileNumberObj = parsePhoneNumberFromString(value, countryIso || undefined);

                if (!mobileNumberObj?.isValid()) {
                    setValidationErrors((prev) => {
                        if (prev[field] === "Please enter a valid phone number.") return prev;
                        return { ...prev, [field]: "Please enter a valid phone number." };
                    });
                }
                else {
                    setValidationErrors((prev) => {
                        if (!(field in prev)) return prev;
                        const updated = { ...prev };
                        delete updated[field];
                        return updated;
                    });
                }
            }
            else {
                setValidationErrors((prev) => {
                    if (!(field in prev)) return prev;
                    const updated = { ...prev };
                    delete updated[field];
                    return updated;
                });
            }
        }
    }, [countryIso]);

    const handleChange = useCallback((field, value) => {
        if (field === "Country") {
            setCountryIso(value?.isoCode || "");
            setStateIso("");

            setFormData((prev) => ({
                ...prev,
                Country: value?.name || "",
                State: "",
                City: ""
            }));

            return;
        }

        if (field === "State") {
            setStateIso(value?.isoCode || "");

            setFormData((prev) => ({
                ...prev,
                State: value?.name || "",
                City: ""
            }));

            return;
        }

        if (field === "City") {
            setFormData((prev) => ({
                ...prev,
                City: value?.name || ""
            }));

            return;
        }

        if (field === "Next_Review") {
            const formatted = dayjs(value).format("YYYY-MM-DD HH:mm:ss");

            setFormData((prev) => ({
                ...prev,
                [field]: formatted
            }));

            return;
        }

        if (field === "Risk_Classification") {
            setFormData((prev) => {
                const originalReviewDate = dayjs(prev?.originalNextReview || prev?.Next_Review || dayjs());
                const updatedReviewDate = originalReviewDate.add(value?.months || 0, "month").format("YYYY-MM-DD HH:mm:ss");

                return {
                    ...prev,
                    [field]: value,
                    Next_Review: updatedReviewDate,
                    originalNextReview: originalReviewDate.format("YYYY-MM-DD HH:mm:ss")
                };
            });

            return;
        }

        setFormData((prev) => ({
            ...prev,
            [field]: typeof value === "string" ? value.trim() === "" ? null : value : value ?? null
        }));

        handleValidation(field, value);
    }, [handleValidation]);

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsFormSubmitted(true);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        if (rowType === "Existing") {
            handleSubmitEditedRow(formData);
        }
        else if (Object.keys(formData).length > 0) {
            handleSubmitAddedRow(formData);
        }
        else {
            handleClose();
        }
    }

    const countryOptions = Country.getAllCountries().map((c) => ({
        ...c,
        id: c.isoCode,
        value: c.name
    }));

    const stateOptions = (countryIso ? State.getStatesOfCountry(countryIso) : []).map((s) => ({
        ...s,
        id: s.isoCode,
        value: s.name
    }));

    const cityOptions = (countryIso && stateIso ? City.getCitiesOfState(countryIso, stateIso) : []).map((c) => ({
        ...c,
        id: c.name,
        value: c.name
    }));

    const selectedCountryObj = countryOptions.find((c) => c.name === formData.Country) || null;
    const selectedStateObj = stateOptions.find((s) => s.name === formData.State) || null;
    const selectedCityObj = cityOptions.find((c) => c.name === formData.City) || null;

    return (
   
            <Modal className="entities form-modal "
                show={show}
                onHide={handleClose}
                centered={true}
                size="lg"
            >
                <Backdrop
                    sx={{ color: '#fff', position: 'absolute', height: '100%', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <Loader />
                </Backdrop>

                <Modal.Header style={{ padding: '7px 16px' }} closeButton={true}>
                    <Modal.Title>
                        {rowType === "Existing" ? `Edit ${title}` : `Add ${title}`}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="w-100">
                    <form onSubmit={handleSubmit} className="w-100">
                        <Grid container spacing={"10px"} className="w-100 align-items-start autocomplete justify-content-between">
                            {columns?.filter((column) => (column.field !== "Created_On" && column.field !== "created_on" && column.field !== "actions"))?.map((column, index) => {
                                const hasError = isFormSubmitted && validationErrors[column.field] !== undefined;
                                const errorMessage = validationErrors[column.field] || "";

                                return (
                                    <Grid size={{ xs:4}} key={index}>
                                        {column.field === "Business_Unit" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="Business Unit"
                                                options={dropdowns["Business_Unit"] || []}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Business_Unit", newValue)}
                                            />
                                        ) : column.field === "Captive_Status" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={false}
                                                isWritable={true}
                                                filterOn="Captive Status"
                                                options={["Captive", "Non-Captive"]}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Captive_Status", newValue)}
                                            />
                                        ) : column.field === "Type_of_Counter_Party" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={false}
                                                isWritable={true}
                                                filterOn="Type of Counter Party"
                                                options={["Manufacturer", "Trader"]}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Type_of_Counter_Party", newValue)}
                                            />
                                        ) : column.field === "City" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="City"
                                                options={cityOptions}
                                                selectedOptions={selectedCityObj}
                                                handleSelectChange={(filterOn, newValue) => handleChange("City", newValue)}
                                            />
                                        ) : column.field === "Contact_Number" ? (
                                            <PhoneInput
                                                ref={phoneInputRef}
                                                className="contact-person-mobile"
                                                placeholder="Contact Info (Mobile No.)"
                                                value={formData[column.field] || ""}
                                                error={hasError}
                                                helperText={hasError ? errorMessage : ""}
                                                onChange={(value) => handleChange("Contact_Number", value)}
                                                inputComponent={CustomPhoneInput}
                                            />
                                        ) : column.field === "Country" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="Country"
                                                options={countryOptions}
                                                selectedOptions={selectedCountryObj}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Country", newValue)}
                                            />
                                        ) : (title !== "Currency" && column.field === "Currency") ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="Currency"
                                                options={dropdowns["Currency"] || []}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Currency", newValue)}
                                            />
                                        ) : column.field === "File_Subtype" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={false}
                                                isWritable={true}
                                                isRequired={true}
                                                filterOn="File Subtype"
                                                options={["External", "KYC"]}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("File_Subtype", newValue)}
                                            />
                                        ) : column.field === "Grade" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="Product Grade"
                                                options={dropdowns["Grade"] || []}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Grade", newValue)}
                                            />
                                        ) : column.field === "KYC_Status" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={false}
                                                isWritable={true}
                                                isRequired={true}
                                                filterOn="Counter Party Status"
                                                options={dropdowns["KYC_Status"] || []}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("KYC_Status", newValue)}
                                            />
                                        ) : column.field === "Next_Review" ? (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DesktopDatePicker
                                                    inputFormat="YYYY-MM-DD HH:mm:ss"
                                                    label="Next Review"
                                                    disableMaskedInput
                                                    className="form-text-field date-picker w-100"
                                                    value={convertToDayJsObject(formData[column.field]) || dayjs()}
                                                    onChange={(newDate) => handleChange("Next_Review", newDate, true)}
                                                    onAccept={(newDate) => handleChange("Next_Review", newDate, true)}
                                                    minDate={dayjs()}
                                                    readOnly={!isWritable}
                                                    slotProps={{
                                                        actionBar: {
                                                            actions: ["clear"]
                                                        },
                                                        textField: {
                                                            InputProps: {
                                                                size: "small",
                                                                required: true,
                                                                disabled: true
                                                            }
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        ) : column.field === "Risk_Classification" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                isRequired={true}
                                                filterOn="Risk Rating"
                                                options={dropdowns["Risk_Classification"] || []}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Risk_Classification", newValue)}
                                            />
                                        ) : column.field === "State" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="State"
                                                options={stateOptions}
                                                selectedOptions={selectedStateObj}
                                                handleSelectChange={(filterOn, newValue) => handleChange("State", newValue)}
                                            />
                                        ) : column.field === "Tab" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={false}
                                                isWritable={true}
                                                filterOn="Tab"
                                                options={["Buying Leg", "Selling Leg"]}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Tab", newValue)}
                                            />
                                        ) : column.field === "Type_of_Packaging" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="Type of Packaging"
                                                options={dropdowns["Type_of_Packaging"] || []}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Type_of_Packaging", newValue)}
                                            />
                                        ) : column.field === "Unit_of_Measurement" ? (
                                            <VirtualizedAutocomplete
                                                isMultiple={false}
                                                isObject={true}
                                                isWritable={true}
                                                filterOn="UoM"
                                                options={dropdowns["Unit_of_Measurement"] || []}
                                                selectedOptions={formData[column.field] || null}
                                                handleSelectChange={(filterOn, newValue) => handleChange("Unit_of_Measurement", newValue)}
                                            />
                                        ) : (
                                            <div className="text-field">
                                                <TextField
                                                    label={column?.field?.replaceAll("_", " ")}
                                                    type={column.type}
                                                    className={`${(title === "Counter Party" && column?.field === "Name") ? 'name-text-uppercase' : ''}`}
                                                    value={formData[column.field] || ""}
                                                    error={hasError}
                                                    helperText={hasError ? errorMessage : ""}
                                                    required={column?.field === "Name"}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        if (title === "Tenor" && column.type === "number" && value !== "" && parseFloat(value) < 0) {
                                                            return;
                                                        }
                                                        handleChange(column.field, value);
                                                    }}
                                                    slotProps={{
                                                        input: { min: 0 }
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (column.type === "number") {
                                                            if (title === "Tenor" && ["-", "+", "e", "E"].includes(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                            else if (title === "Tenor" && event.key === "ArrowDown") {
                                                                const currentValue = parseFloat(formData[column.field]);
                                                                if (!currentValue || currentValue <= 0) {
                                                                    event.preventDefault();
                                                                }
                                                            }
                                                            else if (title !== "Tenor" && ["+", "e", "E"].includes(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>

                        <button ref={submitButtonRef} type="submit" className="hidden"></button>
                    </form>
                </Modal.Body>

                <Modal.Footer className="gap-2">
                    <Button variant="outlined" size="small" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" size="small" onClick={() => submitButtonRef?.current?.click()}>
                        {rowType === "Existing" ? 'Update' : 'Add'}
                    </Button>
                </Modal.Footer>
            </Modal>
    );
}

export default memo(Form);
